import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, CalendarCheck } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { getSupportedEventTypes, getEventTypeConfig } from '../../config/eventTypes';
import { EventAwareDateRangePicker } from '../../components/EventAwareDatePicker';
import InfoTip from '../../components/InfoTip';
import EventHome from './EventHome';

const WEDDING_NAME_JOINER = ' \u2764\uFE0F ';

const EVENT_TYPE_SHORT_DESCRIPTIONS = {
  wedding: 'Plan every wedding detail.',
  corporate: 'Run polished company events.',
  baby_shower: 'Celebrate a joyful new arrival.',
  birthday: 'Make every birthday memorable.',
};

const buildWeddingEventName = (brideName, groomName) => (
  [brideName.trim(), groomName.trim()]
    .filter(Boolean)
    .join(WEDDING_NAME_JOINER)
);

export default function CreateEvent() {
  const navigate = useNavigate();
  const { loadUser, switchEvent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [eventNameEdited, setEventNameEdited] = useState(false);
  const [formData, setFormData] = useState({
    event_type: '',
    name: '',
    bride_name: '',
    groom_name: '',
    host_names: '',
    honoree_names: '',
    milestone_number: '',
    description: '',
    start_date: '',
    end_date: ''
  });
  const eventTypeConfig = getEventTypeConfig(formData.event_type || 'wedding');
  const eventTypes = getSupportedEventTypes();
  const isWedding = formData.event_type === 'wedding';
  const isBabyShower = formData.event_type === 'baby_shower';
  const isBirthday = formData.event_type === 'birthday';
  const generatedWeddingName = buildWeddingEventName(formData.bride_name, formData.groom_name);

  const handleEventTypeChange = (eventTypeId) => {
    setSelectedEventType(eventTypeId);
    setEventNameEdited(false);
    setFormData((current) => ({
      ...current,
      event_type: eventTypeId,
      name: eventTypeId === 'wedding'
        ? buildWeddingEventName(current.bride_name, current.groom_name)
        : current.name,
    }));
  };

  const handleWeddingNameFieldChange = (field, value) => {
    setFormData((current) => {
      const next = { ...current, [field]: value };
      if (!eventNameEdited) {
        next.name = buildWeddingEventName(next.bride_name, next.groom_name);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date) {
      toast.error('Start date is required');
      return;
    }
    if (formData.end_date && formData.end_date < formData.start_date) {
      toast.error('End date cannot be earlier than start date');
      return;
    }
    setLoading(true);
    
    try {
      const eventData = {
        event_type: formData.event_type,
        name: formData.name.trim(),
        bride_name: isWedding ? formData.bride_name.trim() : null,
        groom_name: isWedding ? formData.groom_name.trim() : null,
        host_names: (isBabyShower || isBirthday)
          ? formData.host_names.split(',').map((name) => name.trim()).filter(Boolean)
          : [],
        honoree_names: isBirthday
          ? formData.honoree_names.split(',').map((name) => name.trim()).filter(Boolean)
          : [],
        milestone_number: isBirthday && formData.milestone_number
          ? Number(formData.milestone_number)
          : null,
        description: formData.description,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      const response = await api.post('/events', eventData);
      toast.success('Event created successfully!');

      await switchEvent(response.data.id);
      
      // Reload user to get updated linked_event_ids
      await loadUser();
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EventHome autoOpenLastEvent={false} />
      <Dialog open onOpenChange={(open) => { if (!open) navigate('/home'); }}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Create New Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-5 sm:p-6">
            <div className="flex items-center gap-4 pr-8">
          <Link to="/home">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-1">
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="create-event-title">Create New Event</h1>
            <InfoTip content={eventTypeConfig.createEvent.subtitle} label="About creating an event" />
          </div>
            </div>

        <Card className="overflow-hidden border-0 shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-wedding-sage" />
              {selectedEventType ? 'Event Details' : 'Choose an event type'}
              <InfoTip
                content={selectedEventType ? 'Fill in the basic information about your event.' : 'Choose the kind of event you are planning.'}
                label={selectedEventType ? 'About event details' : 'About event types'}
              />
              </span>
              {selectedEventType && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedEventType(null)}>
                  Change type
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!selectedEventType && (
              <div className="space-y-3" data-testid="event-type-step">
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {eventTypes.map((eventType) => {
                    return (
                      <button
                        key={eventType.id}
                        type="button"
                        onClick={() => handleEventTypeChange(eventType.id)}
                        className="rounded-lg border border-border p-3 text-left transition-all hover:-translate-y-0.5 hover:border-wedding-sage hover:bg-wedding-sage/5 hover:shadow-sm sm:p-4"
                        data-testid={`event-type-${eventType.id}`}
                      >
                        <p className="text-sm font-medium text-wedding-charcoal sm:text-base">{eventType.createEvent.title}</p>
                        <p className="mt-1 text-[0.6875rem] leading-4 text-muted-foreground sm:text-xs">
                          {EVENT_TYPE_SHORT_DESCRIPTIONS[eventType.id] || eventType.createEvent.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {selectedEventType && (
              <>
              {isWedding ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="bride_name">Bride Name *</Label>
                      <Input
                        id="bride_name"
                        placeholder="e.g., Harshada"
                        value={formData.bride_name}
                        onChange={(e) => handleWeddingNameFieldChange('bride_name', e.target.value)}
                        required
                        data-testid="event-bride-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="groom_name">Groom Name *</Label>
                      <Input
                        id="groom_name"
                        placeholder="e.g., Hardik"
                        value={formData.groom_name}
                        onChange={(e) => handleWeddingNameFieldChange('groom_name', e.target.value)}
                        required
                        data-testid="event-groom-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Event Name *</Label>
                    <Input
                      id="name"
                      placeholder={generatedWeddingName || `Bride${WEDDING_NAME_JOINER}Groom`}
                      value={formData.name}
                      onChange={(e) => {
                        setEventNameEdited(true);
                        setFormData({ ...formData, name: e.target.value });
                      }}
                      required
                      data-testid="event-name-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-filled from bride and groom names. You can edit it if needed.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Event Name *</Label>
                    <Input
                      id="name"
                      placeholder={eventTypeConfig.createEvent.namePlaceholder}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="event-name-input"
                    />
                  </div>
                  {isBirthday && (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="honoree_names">Who are we celebrating? *</Label>
                        <Input
                          id="honoree_names"
                          placeholder="e.g., Aarav"
                          value={formData.honoree_names}
                          onChange={(e) => setFormData({ ...formData, honoree_names: e.target.value })}
                          required
                          data-testid="event-honoree-names-input"
                        />
                        <p className="text-xs text-muted-foreground">Separate names with commas for a joint birthday.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="milestone_number">Turning (optional)</Label>
                        <Input
                          id="milestone_number"
                          type="number"
                          min="1"
                          max="150"
                          placeholder="e.g., 10"
                          value={formData.milestone_number}
                          onChange={(e) => setFormData({ ...formData, milestone_number: e.target.value })}
                          data-testid="event-milestone-number-input"
                        />
                        <p className="text-xs text-muted-foreground">Use the age, not a date of birth.</p>
                      </div>
                    </div>
                  )}
                  {(isBabyShower || isBirthday) && (
                    <div className="space-y-2">
                      <Label htmlFor="host_names">Host Names</Label>
                      <Input
                        id="host_names"
                        placeholder="e.g., Aisha, Rohan"
                        value={formData.host_names}
                        onChange={(e) => setFormData({ ...formData, host_names: e.target.value })}
                        data-testid="event-host-names-input"
                      />
                      <p className="text-xs text-muted-foreground">Separate multiple names with commas. These names are shown to guests and used in invitations.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder={eventTypeConfig.createEvent.descriptionPlaceholder}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="event-description-input"
                />
              </div>

              <EventAwareDateRangePicker
                id="event-date-range"
                label="Event dates"
                startLabel="Start date"
                endLabel="End date"
                startValue={formData.start_date}
                endValue={formData.end_date}
                onStartChange={(date) => setFormData((current) => ({ ...current, start_date: date }))}
                onEndChange={(date) => setFormData((current) => ({ ...current, end_date: date }))}
                defaultToEventRange={false}
                required
                data-testid="event-date-range-input"
              />

              <div className="flex gap-3 pt-4">
                <Link to="/home" className="flex-1">
                  <Button type="button" variant="outline" className="w-full">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="flex-1 bg-wedding-sage hover:bg-wedding-sage/90"
                  disabled={loading}
                  data-testid="create-event-submit"
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </Button>
              </div>
              </>
              )}
            </form>
          </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
